import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import api from '../../../api/axios';

import AddUserModal from './AddUserModal';

const UserAccessTab = ({ currentUserRole, currentUserEmail }) => {
  const isAdmin = currentUserRole === 'Admin';
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/v1/settings/users');
      setUsers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter users by search term
  const filteredUsers = users?.filter(user => {
    const name = user?.name?.toLowerCase() || '';
    return name?.includes(searchTerm?.toLowerCase());
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers?.slice(startIndex, startIndex + itemsPerPage);

  const getStatusColor = (status) => {
    return status === 'Joined' ?'bg-success/10 text-success border-success/20' :'bg-warning/10 text-warning border-warning/20';
  };

  const getRoleBadge = (role) => {
    return role === 'Admin' ?'bg-accent/10 text-accent border-accent/20' :'bg-secondary/10 text-secondary border-secondary/20';
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUserId) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/settings/users/${deleteUserId}`);
      setDeleteUserId(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleUserAdded = () => {
    fetchUsers();
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages?.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages?.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages?.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages?.push(i);
        }
      }
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      {/* Header with Add User Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">User Access Management</h3>
          <p className="text-sm text-muted-foreground mt-1">Manage team members and their access levels</p>
        </div>
        {isAdmin && (
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setIsAddUserModalOpen(true)}
          >
            Add User
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search users by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* User Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Name</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Email</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Role</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Status</th>
                {isAdmin && <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="py-3 px-4"><div className="h-4 w-28 bg-muted rounded animate-pulse" /></td>
                    <td className="py-3 px-4"><div className="h-4 w-40 bg-muted rounded animate-pulse" /></td>
                    <td className="py-3 px-4"><div className="h-6 w-20 bg-muted rounded-full animate-pulse" /></td>
                    <td className="py-3 px-4"><div className="h-6 w-16 bg-muted rounded-full animate-pulse" /></td>
                    {isAdmin && <td className="py-3 px-4"><div className="h-4 w-16 bg-muted rounded animate-pulse" /></td>}
                  </tr>
                ))
              ) : paginatedUsers?.length > 0 ? (
                paginatedUsers?.map((user) => (
                  <tr key={user?.id} className="hover:bg-muted/30 transition-base">
                    <td className="py-3 px-4">
                      <span className="text-sm font-medium text-foreground">
                        {user?.name}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-foreground">{user?.email}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium ${getRoleBadge(user?.role)}`}>
                        {user?.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium ${getStatusColor(user?.status)}`}>
                        {user?.status}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-4">
                        {user?.email !== currentUserEmail && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setDeleteUserId(user?.id)}
                              className="p-2 hover:bg-error/10 rounded-lg transition-base"
                              title="Delete user"
                            >
                              <Icon name="Trash2" size={16} className="text-error" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="py-8 text-center text-sm text-muted-foreground">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filteredUsers?.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredUsers?.length)} of {filteredUsers?.length} users
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center justify-center w-8 h-8 border border-border rounded-lg text-sm font-medium text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-base"
                aria-label="Previous page"
              >
                <Icon name="ChevronLeft" size={16} />
              </button>

              {getPageNumbers()?.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`flex items-center justify-center w-8 h-8 border rounded-lg text-sm font-medium transition-base ${
                    currentPage === page
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center justify-center w-8 h-8 border border-border rounded-lg text-sm font-medium text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-base"
                aria-label="Next page"
              >
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onUserAdded={handleUserAdded}
      />

      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteUserId}
        onClose={() => setDeleteUserId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        iconName="Trash2"
        loading={deleting}
      />
    </div>
  );
};

export default UserAccessTab;
